import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Check, Package as PackageIcon, CreditCard } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicPackages() {
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [countries, setCountries] = useState([]);
  const [termsContent, setTermsContent] = useState('');
  
  // Page terms state
  const [showPageTermsModal, setShowPageTermsModal] = useState(false);
  const [pageTerms, setPageTerms] = useState(null);
  
  // Form state
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [digitalId, setDigitalId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const signatureRef = useRef(null);

  useEffect(() => {
    checkPageTermsAcceptance();
    fetchPackages();
    fetchCountries();
  }, []);

  const checkPageTermsAcceptance = async () => {
    // Check if user has already accepted terms
    const hasAccepted = localStorage.getItem('page_terms_accepted');
    if (!hasAccepted) {
      // Fetch page terms
      try {
        const response = await axios.get(`${API}/page-terms`);
        if (response.data) {
          setPageTerms(response.data);
          setShowPageTermsModal(true);
        }
      } catch (error) {
        console.error('Failed to fetch page terms');
      }
    }
  };

  const handleAcceptPageTerms = async () => {
    try {
      // Get user's IP address
      const ipResponse = await axios.get('https://api.ipify.org?format=json').catch(() => ({ data: { ip: 'Unknown' } }));
      const userAgent = navigator.userAgent;
      
      // Record acceptance
      await axios.post(`${API}/page-terms/accept`, {
        ip_address: ipResponse.data.ip,
        user_agent: userAgent
      }).catch(err => console.error('Failed to record terms acceptance:', err));
      
      localStorage.setItem('page_terms_accepted', 'true');
      setShowPageTermsModal(false);
    } catch (error) {
      console.error('Error accepting terms:', error);
      // Still allow user to proceed even if recording fails
      localStorage.setItem('page_terms_accepted', 'true');
      setShowPageTermsModal(false);
    }
  };

  const handleRejectPageTerms = () => {
    window.close();
    // If window.close() doesn't work (browsers may block it), redirect
    setTimeout(() => {
      window.location.href = 'about:blank';
    }, 100);
  };

  const fetchPackages = async () => {
    try {
      const product = searchParams.get('product');
      const url = product ? `${API}/packages?product=${encodeURIComponent(product)}` : `${API}/packages`;
      const response = await axios.get(url);
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to load packages');
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/country-requirements`);
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to load countries');
    }
  };

  const fetchTerms = async (productId) => {
    try {
      const response = await axios.get(`${API}/terms/${productId}`);
      setTermsContent(response.data.content);
    } catch (error) {
      setTermsContent('Terms and conditions not available for this product.');
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    fetchTerms(pkg.product_name);
    setShowCheckoutDialog(true);
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleCheckout = async () => {
    if (!userName || !userEmail || !selectedCountry) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Please provide your signature');
      return;
    }

    // Check if digital ID is required for selected country
    const countryReq = countries.find(c => c.country_code === selectedCountry);
    if (countryReq?.requires_digital_id && !digitalId) {
      toast.error(`Digital ID (${countryReq.digital_id_type}) is required for ${countryReq.country_name}`);
      return;
    }

    setLoading(true);

    try {
      const signatureData = signatureRef.current.toDataURL();
      const originUrl = window.location.origin;

      const response = await axios.post(`${API}/create-checkout-session`, {
        package_id: selectedPackage.id,
        billing_cycle: billingCycle,
        user_name: userName,
        user_email: userEmail,
        country_code: selectedCountry,
        signature_data: signatureData,
        digital_id: digitalId || null,
        terms_accepted: termsAccepted,
        origin_url: originUrl
      });

      // Save email for auto-login after payment
      localStorage.setItem('checkout_email', userEmail);
      localStorage.setItem('checkout_name', userName);

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (pkg) => {
    return billingCycle === 'monthly' ? pkg.monthly_price : pkg.yearly_price;
  };

  const getTotalPrice = (pkg) => {
    return getPrice(pkg) + (pkg.transaction_cost || 0);
  };

  return (
    <>
      {/* Page Terms Modal */}
      <Dialog open={showPageTermsModal} onOpenChange={() => {}}>
        <DialogContent data-testid="page-terms-modal" className="max-w-2xl" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Terms and Conditions</DialogTitle>
            <DialogDescription>
              Please read and accept our terms and conditions to continue
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 max-h-[400px] overflow-y-auto border border-gray-300 rounded-lg p-4 bg-gray-50">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{pageTerms?.content}</p>
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              data-testid="reject-terms-button"
              onClick={handleRejectPageTerms}
              variant="outline"
              className="flex-1"
            >
              Reject & Close
            </Button>
            <Button
              data-testid="accept-terms-button"
              onClick={handleAcceptPageTerms}
              className="flex-1 bg-gray-900 hover:bg-gray-800"
            >
              Accept & Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div data-testid="public-packages-page" className="min-h-screen py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
            Select the perfect subscription package for your needs
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white/10 backdrop-blur-md rounded-full p-1 inline-flex">
            <button
              data-testid="monthly-billing-toggle"
              onClick={() => setBillingCycle('monthly')}
              className={`px-8 py-3 rounded-full font-medium transition-all duration-200 ${
                billingCycle === 'monthly'
                  ? 'bg-white text-indigo-600 shadow-lg'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="yearly-billing-toggle"
              onClick={() => setBillingCycle('yearly')}
              className={`px-8 py-3 rounded-full font-medium transition-all duration-200 ${
                billingCycle === 'yearly'
                  ? 'bg-white text-indigo-600 shadow-lg'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              data-testid={`package-card-${pkg.id}`}
              className="relative overflow-hidden bg-white/95 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-600 to-purple-600" />
              <CardHeader className="space-y-4 pt-8">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <PackageIcon className="w-6 h-6 text-white" />
                  </div>
                  {pkg.transaction_cost > 0 && (
                    <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-medium">
                      +${pkg.transaction_cost.toFixed(2)} fee
                    </span>
                  )}
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold">{pkg.name}</CardTitle>
                  <CardDescription className="mt-2">{pkg.description}</CardDescription>
                </div>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-indigo-600">
                    ${getTotalPrice(pkg).toFixed(2)}
                  </span>
                  <span className="text-gray-500 ml-2">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {pkg.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button
                  data-testid={`select-package-${pkg.id}`}
                  onClick={() => handleSelectPackage(pkg)}
                  className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200"
                >
                  Select Plan
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent data-testid="checkout-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Complete Your Subscription</DialogTitle>
            <DialogDescription>
              {selectedPackage?.name} - ${selectedPackage && getTotalPrice(selectedPackage).toFixed(2)}/{billingCycle === 'monthly' ? 'month' : 'year'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-6">
            {/* User Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="userName">Full Name *</Label>
                <Input
                  data-testid="user-name-input"
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="userEmail">Email Address *</Label>
                <Input
                  data-testid="user-email-input"
                  id="userEmail"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="country">Country *</Label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger data-testid="country-select" className="mt-1">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.country_code} value={country.country_code}>
                        {country.country_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Digital ID (conditional) */}
              {selectedCountry && countries.find(c => c.country_code === selectedCountry)?.requires_digital_id && (
                <div>
                  <Label htmlFor="digitalId">
                    Digital ID ({countries.find(c => c.country_code === selectedCountry)?.digital_id_type}) *
                  </Label>
                  <Input
                    data-testid="digital-id-input"
                    id="digitalId"
                    value={digitalId}
                    onChange={(e) => setDigitalId(e.target.value)}
                    placeholder={`Enter your ${countries.find(c => c.country_code === selectedCountry)?.digital_id_type} number`}
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            {/* Signature */}
            <div>
              <Label>Digital Signature *</Label>
              <div className="mt-2 border-2 border-gray-300 rounded-lg overflow-hidden">
                <SignatureCanvas
                  data-testid="signature-canvas"
                  ref={signatureRef}
                  canvasProps={{
                    className: 'signature-canvas w-full h-40'
                  }}
                />
              </div>
              <Button
                data-testid="clear-signature-button"
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSignature}
                className="mt-2"
              >
                Clear Signature
              </Button>
            </div>

            {/* Terms and Conditions */}
            <div className="space-y-3">
              <Label>Terms and Conditions</Label>
              <div className="border border-gray-300 rounded-lg p-4 max-h-40 overflow-y-auto bg-gray-50">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{termsContent}</p>
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  data-testid="terms-checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                />
                <label
                  htmlFor="terms"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  I accept the terms and conditions *
                </label>
              </div>
            </div>

            {/* Submit */}
            <Button
              data-testid="proceed-to-payment-button"
              onClick={handleCheckout}
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200"
            >
              {loading ? (
                'Processing...'
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}
